import * as React from "react";
import { useField, useFormikContext } from "formik";
import { Box, Slider, TextField, Typography, Stack, useTheme, InputAdornment, Tooltip, IconButton, SxProps, Theme } from "@mui/material";
import { allowDecimalNumbers, allowOnlyNumbers, formatCurrency, toINR } from "../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { InfoIconOutlined } from "../../assets/SvgIcons";
import { useFieldRequired } from "./FormSchemaContext";


type InputModeType =
    | "email"
    | "search"
    | "tel"
    | "text"
    | "url"
    | "none"
    | "numeric"
    | "decimal";

type CustomSliderProps = {
    name: string;
    label: string;
    min?: number;
    max: number;
    step?: number;
    startCharacter?: string;
    endCharacter?: string;
    formatValue?: (v: number) => string;
    parseInput?: (s: string) => number;
    disabled?: boolean;
    inputMode?: InputModeType;
    sx?: SxProps<Theme>;
    onKeyDown?: (e: React.ChangeEvent<HTMLInputElement>) => void
};

export function CustomSlider({
    name,
    label,
    min,
    max,
    step = 1,
    startCharacter,
    endCharacter,
    formatValue,
    parseInput,
    disabled,
    inputMode,
    sx,
    onKeyDown
}: CustomSliderProps): JSX.Element {
    const { setFieldValue, setFieldTouched } = useFormikContext<any>();
    const theme = useTheme();
    const [field, meta] = useField<number | string>(name);
    const errorText = meta.error && meta.touched ? meta.error : '';
    const isRequired = useFieldRequired(name);

    // Mode detection
    const isNumeric = inputMode === "numeric";
    const isDecimal = inputMode === "decimal";
    const isText = !isNumeric && !isDecimal;

    // Value handling: keep number for numeric/decimal, string for text
    const value = field.value;
    // isText
    //     ? (typeof field.value === "string" ? field.value : String(field.value ?? ""))
    //     : (typeof field.value === "number" ? field.value : min);

    // const clamp = (n: number) => Math.min(max, Math.max(min, n));
    const clamp = (n: number | string) => {
        if (!n) return n;
        if (Number(n) > max) return Number(max);
        else return n;
    };

    // Common helpers
    const commitNumber = (n: number | string) => {
        return (
            setFieldValue(name, clamp(n), true)
        )
    };
    const handleBlur = () => setFieldTouched(name, true, true);

    // Slider only makes sense for numeric/decimal
    const handleSliderChange = (_: Event, v: number | number[]) => {
        const next = Array.isArray(v) ? v[0] : v;
        commitNumber(next);
    };

    // Input change per-mode
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        e.preventDefault();
        if (isText) {
            setFieldValue(name, raw, true);
            return;
        }

        if (isNumeric) {
            if (!raw) return commitNumber(raw);
            // const onlyDigits = raw.replace(/[^\d-]/g, "");
            const normalized = raw.replaceAll(",", "");
            const normalizeDecimal = normalized.replaceAll('.', '');
            commitNumber(normalizeDecimal);
            return;
        }

        if (isDecimal) {
            if (!raw) return commitNumber(raw);
            const normalized = raw.replace(",", ".");
            const decimalPart = normalized.split('.')[1];
            // if (!decimalPart) return commitNumber(raw);
            if (decimalPart && decimalPart.length > 2) {
                const truncatedValue = normalized.slice(0, -decimalPart.length + 2);
                commitNumber(Number(truncatedValue))
                e.target.value = truncatedValue;
            } else {
                commitNumber(raw)
            }

            // const parsed = parseInput
            //     ? parseInput(normalized)
            //     : Number(normalized.replace(/[^\d.-]/g, ""));
            // if (!Number.isNaN(parsed)) commitNumber(parsed);
            // return;
        }
    };

    // Input props per-mode
    const inputProps =
        isNumeric
            ? {
                inputMode: "numeric" as const,
                pattern: "[0-9]*",
                min,
                max,
                step: 1,
            }
            : isDecimal
                ? {
                    inputMode: "decimal" as const,
                    // Accept dot-decimal by default; adjust if locale commas are needed
                    pattern: "\\d+(\\.\\d{1,2})?",
                    min,
                    max,
                    step: 0.01, // or "any" if arbitrary precision is required
                }
                : {
                    inputMode: "text" as const,
                };

    // HTML input type per-mode
    const htmlType = isText ? "text" : "number";

    return (
        <Box sx={{ display: "flex", flexDirection: "column", padding: "0px 10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="body_500">{label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {errorText && (
                        <Tooltip
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'preventOverflow',
                                            options: {
                                                boundary: 'viewport',
                                                padding: 8,
                                            },
                                        },
                                    ],
                                },
                                tooltip: {
                                    sx: {
                                        margin: "0px !important",
                                        backgroundColor: "transparent",
                                        maxWidth: { xs: '300px', sm: 'none' },
                                    }
                                }
                            }}
                            title={
                                <Box sx={{
                                    borderRadius: '10px',
                                    backgroundColor: theme.palette.common.white,
                                    padding: '10px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    rowGap: '10px',
                                    maxHeight: { xs: '70vh', sm: 'none' },
                                    overflowY: 'auto',
                                    boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.05)'
                                    }
                                }}>
                                    <Typography variant="overline_400" sx={{ color: theme.palette.error[500] }}>{errorText}</Typography>
                                </Box>
                            }
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        '@media (pointer: coarse)': {
                                            touchAction: 'none',
                                            position: 'fixed',
                                            zIndex: 9999
                                        }
                                    }
                                }
                            }}
                            disableInteractive={false}
                            enterTouchDelay={0}
                            leaveTouchDelay={3000}
                        >
                            <IconButton
                                sx={{
                                    '@media (pointer: coarse)': {
                                        padding: '12px'
                                    }
                                }}
                            >
                                <InfoIconOutlined />
                            </IconButton>
                        </Tooltip>
                    )}
                    <TextField
                        type={'text'}
                        value={value as any}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        size="small"
                        onKeyDown={(e) => {
                            allowDecimalNumbers(e)
                        }}
                        inputProps={{
                            ...inputProps,
                            'aria-required': isRequired || undefined,
                        }}
                        InputProps={{
                            startAdornment:
                                startCharacter && <InputAdornment position="start">{startCharacter}</InputAdornment>,
                            endAdornment:
                                endCharacter && <InputAdornment position="end">{endCharacter}</InputAdornment>,
                        }}
                        disabled={disabled}
                        sx={{
                            "& .MuiTypography-root": { color: errorText ? theme.palette.error[500] : theme.palette.primary[500] },
                            "& .MuiOutlinedInput-root": { p: 0 },
                            "& .MuiOutlinedInput-input": {
                                textAlign: startCharacter ? "left" : "right",
                                color: errorText ? theme.palette.error[500] : theme.palette.primary[500],
                            },
                            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                            "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                            maxWidth: 120,
                            backgroundColor: errorText ? theme.palette.error[50] : theme.palette.grey[200],
                            padding: "1px 10px",
                            borderRadius: "10px",
                            ...sx
                        }}
                    />
                </Box>
            </Box>

            {!isText && (
                <Slider
                    value={Number(value)}
                    min={min}
                    max={max}
                    step={step}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleBlur}
                    aria-labelledby={`${name}-slider`}
                    aria-required={isRequired || undefined}
                    name={name}
                    disabled={disabled}
                    sx={{
                        "& .MuiSlider-thumb": {
                            transition: "transform 120ms ease",
                            width: 20,
                            height: 20,
                        },
                        height: 10,
                        "& .MuiSlider-rail": { height: 6, opacity: 0.5 },
                        "& .MuiSlider-track": { height: 5 },
                    }}
                />
            )}
        </Box>
    );
};

